/**
 * Shared SVG paint servers for gradient-filled icons.
 *
 * WHY this exists: CSS cannot create an SVG paint server. To fill a glyph with the
 * AI gradient, `fill` has to point at a real `<radialGradient>` element that lives
 * somewhere in the same document — `fill: linear-gradient(...)` is not valid SVG
 * paint. So `.seeds-icon-gradient-ai` in icon.css resolves to
 * `fill: url(#seeds-gradient-ai)`, and this module makes sure that ID exists.
 *
 * WHY it is injected here rather than shipped in the sprite: the four icon sprites
 * (general/sprout/external/ai) are appended independently by each app's asset
 * loader. Putting the defs in all four produces duplicate IDs; putting them in one
 * silently no-ops for apps that don't load that particular sprite; and a separate
 * exported sprite string would force every consumer to update their loadAssets.
 * Self-injecting keeps this zero-config.
 *
 * Not part of the public export surface (not re-exported from index.ts).
 */

export const GRADIENT_AI_ID = "seeds-gradient-ai";

const CONTAINER_ID = "seeds-icon-gradient-defs";

/**
 * Geometry note: `userSpaceOnUse` with an 18x18 frame, centered bottom-left, is the
 * SVG equivalent of `radial-gradient(circle at bottom left, ...)` and matches the
 * transform Figma baked into the legacy `*-gradient-*.svg` exports. 764 of the 797
 * icons use a `0 0 18 18` viewBox; the ~33 wider ones (up to 24) simply pad the
 * final stop across their right edge.
 *
 * `objectBoundingBox` (the SVG default) would be wrong here: it resolves against
 * each *path's* bounding box, so a multi-path glyph like `sparkle-solid` would get a
 * separate full gradient sweep per path instead of one across the whole icon.
 *
 * Stops carry classes instead of `stop-color` attributes because `var()` does not
 * resolve inside SVG presentation attributes. icon.css maps the classes onto
 * `--color-gradient-ai-stop-{1,2,3}`, which gives dark mode for free via `.dark`.
 */
const GRADIENT_DEFS_MARKUP = `<svg id="${CONTAINER_ID}" aria-hidden="true" focusable="false" width="0" height="0" style="position:absolute;width:0;height:0;overflow:hidden" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="${GRADIENT_AI_ID}" gradientUnits="userSpaceOnUse" cx="0" cy="18" r="26"><stop class="seeds-gradient-ai-stop-1" offset="0"/><stop class="seeds-gradient-ai-stop-2" offset="0.61"/><stop class="seeds-gradient-ai-stop-3" offset="1"/></radialGradient></defs></svg>`;

/**
 * Append the gradient defs to <body> once per document. Safe to call on every Icon
 * mount: it no-ops when the container is already present, and when there is no DOM
 * (SSR). Uses the same detached-template approach as the reference site's
 * loadAssets.ts.
 */
export function ensureGradientDefs(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(CONTAINER_ID)) return;

  const template = document.createElement("template");
  template.innerHTML = GRADIENT_DEFS_MARKUP;
  const node = template.content.firstChild;
  if (node) document.body.append(node);
}
